// Travel: daily arrivals/departures board + travel records register.
import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { RegisterPage, FieldSpec, useLookup, dateCol, boolCol } from '../crud';
import { Tabs, DateField, Spinner, ErrorBox } from '../ui';
import { todayDarwin, toAus } from '../util';

export default function Travel() {
  const [tab, setTab] = useState('board');
  return (
    <div className="page">
      <Tabs active={tab} onSelect={setTab} tabs={[
        { key: 'board', label: 'Daily arrivals & departures board' },
        { key: 'records', label: 'Travel records' }
      ]} />
      {tab === 'board' ? <Board /> : <Records />}
    </div>
  );
}

function Board() {
  const [date, setDate] = useState(todayDarwin());
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setData(null);
    api(`/board?date=${date}`).then(setData).catch(e => setError(e.message));
  }, [date]);
  if (error) return <ErrorBox error={error} />;
  return (
    <div>
      <div className="page-head">
        <h2>Travel board — {toAus(date)}</h2>
        <div className="btn-row">
          <div style={{ width: 140 }}><DateField value={date} onChange={v => v && setDate(v)} /></div>
          <button className="btn" onClick={() => window.print()}>Print</button>
        </div>
      </div>
      {!data ? <Spinner /> : (
        <div className="two-col">
          {(['arrivals', 'departures'] as const).map(dir => (
            <div key={dir} className="panel">
              <h3>{dir === 'arrivals' ? '→ Arrivals' : '← Departures'} ({data[dir].length})</h3>
              {data[dir].length === 0 && <p className="muted">None recorded for this date.</p>}
              {data[dir].map((t: any) => (
                <div key={t.id} style={{ fontSize: 13, marginBottom: 8, borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>
                  <strong>{t.name}</strong> — {t.method || 'travel'} {t.flight_number || ''} {t.travel_time || ''}
                  <div className="muted">
                    {t.from_location || '?'} → {t.to_location || '?'}
                    {t.pickup_required ? ' · airport pickup required' : ''}
                    {t.dropoff_required ? ' · airport drop-off required' : ''}
                    {t.vehicle ? ` · vehicle: ${t.vehicle}` : ''}{t.driver ? ` · driver: ${t.driver}` : ''}
                  </div>
                  {t.notes && <div className="muted">{t.notes}</div>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Records() {
  const people = useLookup('people');
  const specs: FieldSpec[] = [
    { name: 'person_id', label: 'Person', type: 'select', lookup: 'people', required: true },
    { name: 'direction', label: 'Direction', type: 'select', options: ['arrival', 'departure'], required: true, default: 'arrival' },
    { name: 'travel_date', label: 'Travel date', type: 'date', required: true },
    { name: 'method', label: 'Travel method', type: 'select', options: ['Flight', 'Drive', 'Bus', 'Other'] },
    { name: 'flight_number', label: 'Flight number', type: 'text' },
    { name: 'from_location', label: 'Departure location', type: 'text' },
    { name: 'to_location', label: 'Arrival location', type: 'text' },
    { name: 'travel_time', label: 'Time (e.g. 10:35)', type: 'text' },
    { name: 'vehicle', label: 'Vehicle', type: 'text' },
    { name: 'driver', label: 'Driver', type: 'text' },
    { name: 'pickup_required', label: 'Airport pickup required', type: 'bool' },
    { name: 'dropoff_required', label: 'Airport drop-off required', type: 'bool' },
    { name: 'notes', label: 'Transport notes', type: 'textarea' }
  ];
  return <RegisterPage title="Travel records" entity="travel" specs={specs} lookupEntities={{ people }} canDelete columns={[
    { key: 'person_id', label: 'Person', render: r => <strong>{people.byId[r.person_id] || ''}</strong>, sortValue: r => people.byId[r.person_id] },
    { key: 'direction', label: 'Direction' },
    dateCol('travel_date', 'Date'),
    { key: 'method', label: 'Method' },
    { key: 'flight_number', label: 'Flight' },
    { key: 'travel_time', label: 'Time' },
    boolCol('pickup_required', 'Pickup'),
    boolCol('dropoff_required', 'Drop-off')
  ]} />;
}
